import request from '../utils/Request';
import { Methods, NotificationType } from '../config';
import addNotification from '../utils/addNotification';
import { downloadFile } from '../utils/download';
import dayjs from 'dayjs';
import { ResponseType } from '../config/responetype';

const baseURL = 'project';

const projectService = {
  getProjects: (data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/projects`,
      data,
    });
  },

  getProjectById: (projectId) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/${encodeURIComponent(projectId)}`,
    });
  },

  createProject: (data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}`,
      formData: true,
      data,
    });
  },

  updateProject: (data, projectId) => {
    return request({
      method: Methods.PUT,
      resource: `${baseURL}/${encodeURIComponent(projectId)}`,
      formData: true,
      data,
    });
  },

  updateProjectData: (data, projectId) => {
    return request({
      method: Methods.PUT,
      resource: `${baseURL}/${encodeURIComponent(projectId)}`,
      data,
    });
  },

  updateProjectField: (data, projectId) => {
    return request({
      method: Methods.PATCH,
      resource: `${baseURL}/${encodeURIComponent(projectId)}/field`,
      data,
    });
  },

  updateProjectTag: (projectId, tagId) => {
    return request({
      method: Methods.PATCH,
      resource: `${baseURL}/${encodeURIComponent(projectId)}/tag/${encodeURIComponent(tagId)}`,
    });
  },

  uploadProjectAttachment: (projectId, file, folderId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folderId', folderId == null ? '' : String(folderId));

    return request({
      method: Methods.POST,
      resource: `projects/${encodeURIComponent(projectId)}/attachments`,
      formData: true,
      data: formData,
    });
  },

  moveProjectAttachment: (projectId, attachmentId, data) => {
    return request({
      method: Methods.PATCH,
      resource: `projects/${encodeURIComponent(projectId)}/attachments/${encodeURIComponent(attachmentId)}/move`,
      data,
    });
  },

  deleteProjectAttachment: (projectId, attachmentId) => {
    return request({
      method: Methods.DELETE,
      resource: `projects/${encodeURIComponent(projectId)}/attachments/${encodeURIComponent(attachmentId)}`,
    });
  },

  deleteProjectAttachments: (projectId, attachmentIds) => {
    return request({
      method: Methods.DELETE,
      resource: `projects/${encodeURIComponent(projectId)}/attachments`,
      data: { attachmentIds },
    });
  },

  createProjectFolder: (projectId, data) => {
    return request({
      method: Methods.POST,
      resource: `projects/${encodeURIComponent(projectId)}/folders`,
      data,
    });
  },

  importProjectFolder: (projectId, data) => {
    return request({
      method: Methods.POST,
      resource: `projects/${encodeURIComponent(projectId)}/folders/import`,
      data,
    });
  },

  updateProjectFolder: (projectId, folderId, data) => {
    return request({
      method: Methods.PATCH,
      resource: `projects/${encodeURIComponent(projectId)}/folders/${encodeURIComponent(folderId)}`,
      data,
    });
  },

  deleteProjectFolder: (projectId, folderId) => {
    return request({
      method: Methods.DELETE,
      resource: `projects/${encodeURIComponent(projectId)}/folders/${encodeURIComponent(folderId)}`,
    });
  },

  updateProjectJokerChar: (data) => {
    return request({
      method: Methods.PATCH,
      resource: `${baseURL}/projectJokerChar`,
      data,
    });
  },

  deleteProject: (data, projectId) => {
    return request({
      method: Methods.DELETE,
      resource: `${baseURL}/${encodeURIComponent(projectId)}`,
      data,
    });
  },

  orderProject: (projectId, data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/orderProject/${encodeURIComponent(projectId)}`,
      data,
    });
  },

  getAllPrice: (projectId) => {
    return request({
      method: Methods.GET,
      resource: `${baseURL}/allPrice/${encodeURIComponent(projectId)}`,
    });
  },

  getHistoryItemById: (type, id) => {
    return request({
      method: Methods.GET,
      resource: `${baseURL}/${encodeURIComponent(type)}/${encodeURIComponent(id)}`,
    });
  },

  getExcelTemplate: async () => {
    const excelFromServer = await request({
      responseType: 'blob',
      resource: `${baseURL}/import/template/exceltemplate`,
    });
    return downloadFile(excelFromServer, `Projekt_template-${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
  },

  getExcelData: async () => {
    const excelFromServer = await request({
      responseType: 'blob',
      resource: `${baseURL}/import/data/exceldata`,
    });
    return downloadFile(excelFromServer, `Projekt-${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
  },

  importExcel: async (files, userId) => {
    const formData = new FormData();

    for (const file of files) {
      formData.append('files', file);
    }
    formData.append('userId', userId);

    const resp = await request({
      method: Methods.POST,
      resource: `${baseURL}/import/excelimport`,
      formData: true,
      data: formData,
      responseType: 'blob',
    });

    if (resp?.type === ResponseType.xlsx) {
      addNotification({
        content: 'Az import sikertelen!',
        type: NotificationType.ERROR,
      });

      downloadFile(resp, `ERR_${files[0].name}`);
      return Promise.reject(new Error('Error in uploaded file!'));
    }

    const reader = new FileReader();
    reader.readAsText(resp);
    reader.onload = () => {
      const jsonData = JSON.parse(reader.result);
      if (jsonData?.error?.message) {
        return addNotification({
          content: jsonData.error.message,
          type: NotificationType.ERROR,
        });
      }
      return jsonData.data;
    };
  },

  getNextOrderNumber: async (data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/nextOrderNumber`,
      data,
    });
  },

  getPrices: (itemCode, projectId) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/project/prices`,
      data: { itemCode, projectId },
    });
  },

  savePrices: (data) => {
    return request({
      method: Methods.PUT,
      resource: `${baseURL}/project/prices`,
      data,
    });
  },

  getResourceOneRoles: (projectResourceOneId) => {
    return request({
      method: Methods.GET,
      resource: `${baseURL}/resourceOne/roles/${encodeURIComponent(projectResourceOneId)}`,
    });
  },

  saveResourceOneRoles: (data) => {
    return request({
      method: Methods.PUT,
      resource: `${baseURL}/resourceOne/roles`,
      data,
    });
  },
};

export default projectService;
